import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiHealthTrackerService } from '../health-tracker.service';
import {
  BodyBatteryDayResponse,
  BodyBatteryRangeResponse,
  BodyBatteryRequestParamsDay,
  BodyBatteryRequestParamsRange
} from './body-battery.interface';

/**
 * @description Body Battery Service.
 * @exports
 * @class ApiHealthTrackerBodyBatteryService
 */
@Injectable()
export class ApiHealthTrackerBodyBatteryService {
  private readonly apiHealthTrackerService = inject(ApiHealthTrackerService);

  /**
   * Fetches body battery data for a specific day.
   *
   * @param {BodyBatteryRequestParamsDay} params - The parameters for the request.
   * @returns {Observable<BodyBatteryDayResponse>} An observable containing the body battery data for the specified day.
   */
  fetchDayData(
    params: BodyBatteryRequestParamsDay
  ): Observable<BodyBatteryDayResponse> {
    return this.apiHealthTrackerService
      .get<BodyBatteryDayResponse>('/body-battery/day', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
  /**
   * Fetches body battery data for a specific month.
   *
   * @param {BodyBatteryRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<BodyBatteryRangeResponse>} An observable containing the body battery data for the specified month.
   */
  fetchMonthData(
    params: BodyBatteryRequestParamsRange
  ): Observable<BodyBatteryRangeResponse> {
    return this.apiHealthTrackerService
      .get<BodyBatteryRangeResponse>('/body-battery/month', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }

  /**
   * Fetches body battery data for a specific week.
   *
   * @param {BodyBatteryRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<BodyBatteryRangeResponse>} An observable containing the body battery data for the specified week.
   */
  fetchWeekData(
    params: BodyBatteryRequestParamsRange
  ): Observable<BodyBatteryRangeResponse> {
    return this.apiHealthTrackerService
      .get<BodyBatteryRangeResponse>('/body-battery/week', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }

  /**
   * Fetches body battery data for a specific year.
   *
   * @param {BodyBatteryRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<BodyBatteryRangeResponse>} An observable containing the body battery data for the specified year.
   */
  fetchYearData(
    params: BodyBatteryRequestParamsRange
  ): Observable<BodyBatteryRangeResponse> {
    return this.apiHealthTrackerService
      .get<BodyBatteryRangeResponse>('/body-battery/year', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
}
